import React from 'react';
import { motion } from 'framer-motion';
import { Zap } from 'lucide-react';

const stats = [
  { val: '$4.2M+', label: 'Client Revenue Generated' },
  { val: '85,000+', label: 'Tasks Automated Weekly' },
  { val: '99.98%', label: 'Production Uptime' },
  { val: '3.2x', label: 'Average Client ROI' },
];

const cases = [
  {
    client: 'Nexus Supply Chain',
    tag: 'Workflow Automation',
    industry: 'Supply Chain & Freight (Austin, TX)',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80',
    problem: 'Manual dispatch coordination was causing shipping delays and consuming 35+ hours of manual labor per week.',
    solution: 'Built an automated dispatch engine with real-time route optimization, driver notification pipelines, and ERP sync.',
    result: '40% faster dispatch operations and zero shipment delays within 60 days of release.',
    metrics: [
      { val: '40%', label: 'Faster Ops' },
      { val: '35h', label: 'Saved/Wk' },
      { val: '0', label: 'Delays' },
    ],
    accent: '#0284C7',
  },
  {
    client: 'Apex FinTech Capital',
    tag: 'AI Agents & Chatbots',
    industry: 'Financial Technology (Toronto, ON)',
    image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?auto=format&fit=crop&w=800&q=80',
    problem: 'Inbound leads were dropping off during financial onboarding due to slow response times outside business hours.',
    solution: 'Engineered 24/7 AI agents trained on compliance standards to pre-qualify leads and automate document collection.',
    result: '150% increase in qualified lead conversions and 18% higher customer lifetime value.',
    metrics: [
      { val: '150%', label: 'Conversion ↑' },
      { val: '24/7', label: 'Coverage' },
      { val: '18%', label: 'LTV ↑' },
    ],
    accent: '#4F46E5',
  },
];

const CaseStudies = () => {
  return (
    <section id="cases" className="py-24 bg-[#FFFFFF] text-slate-900 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="section-label mb-3 justify-center">Verified Results &amp; Proof</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">
            Custom software that delivers <span className="text-sky-600">measurable returns.</span>
          </h2>
          <p className="text-slate-600 text-base">
            Here is how our engineered systems drive efficiency and revenue for North American enterprises.
          </p>
        </div>

        {/* Real Numbers Stats Banner */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {stats.map((s, idx) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.08, duration: 0.4 }}
              className="bento-card-light p-6 text-center bg-slate-50"
            >
              <div className="font-heading text-3xl sm:text-4xl font-extrabold text-sky-600 mb-1">
                {s.val}
              </div>
              <div className="text-xs font-mono text-slate-600 font-medium">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Case Studies Bento Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {cases.map((c, idx) => (
            <motion.div
              key={c.client}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ delay: idx * 0.12, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="bento-card-light group flex flex-col justify-between"
            >
              {/* Image Banner */}
              <div className="relative aspect-[16/9] w-full overflow-hidden">
                <img
                  src={c.image}
                  alt={c.client}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 opacity-95"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/10 to-transparent opacity-85" />
                
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                  <span className="text-xs font-mono font-semibold px-3 py-1 rounded-full bg-white/90 backdrop-blur-md border border-slate-200 text-sky-700 uppercase tracking-wider shadow-sm">
                    {c.tag}
                  </span>
                  <span className="text-xs font-mono text-slate-700 bg-white/80 px-2.5 py-1 rounded-md">
                    {c.industry}
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 sm:p-8 flex flex-col flex-1 justify-between bg-white">
                <div>
                  <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">
                    {c.client}
                  </h3>

                  {/* Metrics Banner */}
                  <div className="grid grid-cols-3 gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200 mb-6">
                    {c.metrics.map((m) => (
                      <div key={m.label} className="text-center">
                        <div className="font-heading text-lg font-bold text-sky-600">
                          {m.val}
                        </div>
                        <div className="text-[11px] font-mono text-slate-500">
                          {m.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Problem & Solution */}
                  <div className="space-y-4 mb-6 text-sm">
                    <div>
                      <span className="text-xs font-mono uppercase tracking-wider text-rose-600 font-semibold block mb-1">
                        Challenge
                      </span>
                      <p className="text-slate-600 leading-relaxed font-normal">{c.problem}</p>
                    </div>
                    <div>
                      <span className="text-xs font-mono uppercase tracking-wider text-sky-600 font-semibold block mb-1">
                        System Built
                      </span>
                      <p className="text-slate-600 leading-relaxed font-normal">{c.solution}</p>
                    </div>
                  </div>
                </div>

                {/* Verified Result Banner */}
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-semibold flex items-start gap-3">
                  <Zap size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{c.result}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default CaseStudies;